/**
 * VERIFY OPERATIONAL LINEAGE GRAPH
 *
 * Recompute graph hash, verify all bindings, detect orphans and broken edges.
 * Fail closed on any violation.
 */

import type { OperationalBundleV1 } from '../../server/export/operationalBundleTypes.js';
import type {
  OperationalLineageGraph,
  LineageVerificationResult,
} from './operationalLineageTypes.js';
import { computeLineageGraphHash } from './operationalLineageHash.js';

export async function verifyOperationalLineageGraph(
  graph: OperationalLineageGraph,
  bundle: OperationalBundleV1
): Promise<LineageVerificationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];

  // 1. Verify graph version
  if (graph.graphVersion !== 'operational-lineage-graph/v1') {
    errors.push(`Unsupported lineage graph version: ${graph.graphVersion}`);
  }

  // 2. Recompute graph hash
  const { graphHash: _, ...graphWithoutHash } = graph;
  const recomputedHash = await computeLineageGraphHash(graphWithoutHash);
  const graphHashMatch = recomputedHash === graph.graphHash;
  if (!graphHashMatch) {
    errors.push('Graph hash mismatch: graph may have been tampered with');
  }

  // 3. Bundle hash match
  const bundleHashMatch = graph.bundleHash === bundle.bundleHash;
  if (!bundleHashMatch) {
    errors.push('Graph bundleHash does not match provided bundle');
  }

  // 4. Pane hash bindings
  const paneHashesInBundle = new Map(bundle.paneModelHashes.map((h) => [h.paneId, h.paneHash]));
  let paneHashBindingsValid = true;
  for (const node of graph.nodes) {
    if (node.type === 'pane_model') {
      const paneId = node.metadata.paneId as string;
      const expectedHash = paneHashesInBundle.get(paneId);
      if (!expectedHash) {
        errors.push(`Pane model node references unknown paneId: ${paneId}`);
        paneHashBindingsValid = false;
      } else if (node.hash !== expectedHash) {
        errors.push(`Pane model hash mismatch for ${paneId}`);
        paneHashBindingsValid = false;
      }
    }
  }

  // 5. Attestation binding
  let attestationBindingValid = true;
  if (bundle.signature && bundle.signedBundleHash) {
    const attestationNodes = graph.nodes.filter((n) => n.type === 'attestation');
    if (attestationNodes.length === 0) {
      errors.push('Bundle is signed but lineage graph has no attestation node');
      attestationBindingValid = false;
    } else {
      for (const attNode of attestationNodes) {
        if (attNode.hash !== bundle.signedBundleHash) {
          errors.push('Attestation node hash does not match bundle signedBundleHash');
          attestationBindingValid = false;
        }
      }
    }
  }

  // 6. Replay window binding
  let replayWindowBindingValid = true;
  if (bundle.replayWindows && bundle.replayWindows.length > 0) {
    const replayNodes = graph.nodes.filter((n) => n.type === 'replay_window');
    const replayIds = new Set(replayNodes.map((n) => n.metadata.windowId as string));
    for (const rw of bundle.replayWindows) {
      if (!replayIds.has(rw.windowId)) {
        errors.push(`Replay window ${rw.windowId} missing from lineage graph`);
        replayWindowBindingValid = false;
      }
    }
  }

  // 7. Orphaned nodes
  const edgeSources = new Set(graph.edges.map((e) => e.source));
  const edgeTargets = new Set(graph.edges.map((e) => e.target));
  const connectedNodeIds = new Set([...edgeSources, ...edgeTargets]);
  const orphanedNodes = graph.nodes.filter((n) => !connectedNodeIds.has(n.id) && n.type !== 'bundle');
  if (orphanedNodes.length > 0) {
    warnings.push(`Orphaned nodes detected: ${orphanedNodes.map((n) => n.type).join(', ')}`);
  }
  const noOrphanedNodes = orphanedNodes.length === 0;

  // 8. Broken edges (source or target missing)
  const nodeIds = new Set(graph.nodes.map((n) => n.id));
  const brokenEdges = graph.edges.filter((e) => !nodeIds.has(e.source) || !nodeIds.has(e.target));
  if (brokenEdges.length > 0) {
    errors.push(`Broken edges detected: ${brokenEdges.length}`);
  }
  const noBrokenEdges = brokenEdges.length === 0;

  const valid =
    errors.length === 0 &&
    graphHashMatch &&
    bundleHashMatch &&
    paneHashBindingsValid &&
    attestationBindingValid &&
    replayWindowBindingValid &&
    noBrokenEdges;

  return {
    valid,
    graphHashMatch,
    bundleHashMatch,
    paneHashBindingsValid,
    attestationBindingValid,
    replayWindowBindingValid,
    noOrphanedNodes,
    noBrokenEdges,
    errors,
    warnings,
  };
}
